import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CalendarCheck, ImagePlus, Loader2, Lock, Mail, Phone, RefreshCw, Trash2 } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const LeadCard = ({ lead, onBook, onUnbook }) => {
  const [date, setDate] = useState(lead.preferred_date || "");
  return (
    <article className="border border-white/10 bg-panel p-5" data-testid={`lead-${lead.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-display text-xl font-bold uppercase tracking-wide text-white">{lead.full_name}</h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{fmtDate(lead.created_at)}</span>
      </div>
      <p className="mt-1 text-sm text-slate-300">
        {lead.vehicle_year} {lead.vehicle_make} {lead.vehicle_model} — {lead.vehicle_type} — {lead.condition}
      </p>
      <p className="mt-1 text-sm text-slate-400">{lead.location}{lead.preferred_date ? ` — prefers ${lead.preferred_date}` : ""}</p>
      <div className="mt-3 flex flex-wrap gap-4 font-mono text-xs tracking-wider">
        <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 text-blue-400 hover:text-blue-300">
          <Phone size={13} /> {lead.phone}
        </a>
        <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 text-blue-400 hover:text-blue-300">
          <Mail size={13} /> {lead.email}
        </a>
      </div>
      {lead.message && <p className="mt-3 border-l-2 border-blue-600/60 bg-white/5 px-3 py-2 text-sm text-slate-300">{lead.message}</p>}
      <div className="mt-4 border-t border-white/10 pt-4">
        {lead.appointment_date ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-2 text-sm text-slate-200">
              <CalendarCheck size={15} className="text-blue-400" aria-hidden="true" />
              Booked — {lead.appointment_date}
              <span className={`font-mono text-[9px] uppercase tracking-[0.18em] ${lead.reminder_sent ? "text-green-400" : "text-slate-500"}`}>
                {lead.reminder_sent ? "Reminder sent" : "Day-before reminder scheduled"}
              </span>
            </p>
            <button onClick={() => onUnbook(lead.id)} className="border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 hover:border-red-500/50 hover:text-red-300" data-testid={`lead-unbook-${lead.id}`}>
              Remove Booking
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor={`book-date-${lead.id}`} className="sr-only">Appointment date</label>
            <input
              id={`book-date-${lead.id}`}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="field-input !w-auto !py-2 !text-xs [color-scheme:dark]"
              data-testid={`lead-book-date-${lead.id}`}
            />
            <button onClick={() => date && onBook(lead.id, date)} className="flex items-center gap-1.5 bg-steel px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white hover:bg-blue-500" data-testid={`lead-book-${lead.id}`}>
              <CalendarCheck size={13} /> Mark Booked
            </button>
          </div>
        )}
      </div>
    </article>
  );
};

const Admin = () => {
  const [authed, setAuthed] = useState(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [label, setLabel] = useState("");
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useSeo({ title: "Owner Dashboard | Freedom Mobile Auto Detailing", path: "/admin", robots: "noindex, nofollow" });

  useEffect(() => {
    fetch(`${API}/admin/verify`, { credentials: "include" })
      .then((r) => setAuthed(r.ok))
      .catch(() => setAuthed(false));
  }, []);

  const loadLeads = () =>
    fetch(`${API}/admin/leads`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setLeads(d.leads || []))
      .catch(() => toast.error("Couldn't load quote requests"));

  const loadPhotos = () =>
    fetch(`${API}/admin/photos`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setPhotos(d.photos || []))
      .catch(() => toast.error("Couldn't load photos"));

  useEffect(() => {
    if (authed) {
      loadLeads();
      loadPhotos();
    }
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    const res = await fetch(`${API}/admin/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ pin }),
    });
    if (res.ok) {
      setAuthed(true);
      toast.success("Welcome back");
    } else {
      const d = await res.json().catch(() => ({}));
      setError(typeof d.detail === "string" ? d.detail : "Incorrect PIN");
    }
  };

  const logout = async () => {
    await fetch(`${API}/admin/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    setAuthed(false);
    setPin("");
  };

  const bookLead = async (id, appointment_date) => {
    const res = await fetch(`${API}/admin/leads/${id}/book`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ appointment_date }),
    });
    if (res.ok) {
      toast.success("Booked — reminder scheduled for the day before");
      loadLeads();
    } else {
      toast.error("Couldn't save booking");
    }
  };

  const unbookLead = async (id) => {
    const res = await fetch(`${API}/admin/leads/${id}/unbook`, { method: "POST", credentials: "include" });
    if (res.ok) {
      toast.success("Booking removed");
      loadLeads();
    } else {
      toast.error("Couldn't remove booking");
    }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!beforeFile || !afterFile || !label.trim()) {
      toast.error("Add a label and both photos");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("label", label.trim());
    fd.append("before", beforeFile);
    fd.append("after", afterFile);
    try {
      const res = await fetch(`${API}/admin/photos`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "Upload failed");
      }
      toast.success("Photos published to Real Results");
      setLabel("");
      setBeforeFile(null);
      setAfterFile(null);
      e.target.reset();
      loadPhotos();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (id) => {
    const res = await fetch(`${API}/admin/photos/${id}`, { method: "DELETE", credentials: "include" });
    if (res.ok) {
      toast.success("Photo removed");
      loadPhotos();
    } else {
      toast.error("Couldn't remove photo");
    }
  };

  return (
    <div className="min-h-screen bg-obsidian" data-testid="admin-page">
      <header className="border-b border-white/10">
        <div className="mx-auto flex h-[72px] max-w-5xl items-center justify-between px-5 sm:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-brand-link">
            <span className="flex h-9 w-9 items-center justify-center border border-blue-500/50 font-display text-xl font-bold text-white">F</span>
            <span className="leading-none">
              <span className="block font-display text-xl font-bold uppercase tracking-[0.08em] text-white">Freedom</span>
              <span className="mt-1 block font-mono text-[9px] font-semibold uppercase tracking-[0.3em] text-slate-400">Owner Dashboard</span>
            </span>
          </Link>
          {authed ? (
            <button onClick={logout} className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300 hover:text-blue-400" data-testid="admin-logout">
              Log Out
            </button>
          ) : (
            <Link to="/" className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300 hover:text-blue-400" data-testid="admin-back-link">
              <ArrowLeft size={14} /> Back to Site
            </Link>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-14 sm:px-8">
        {authed === null && (
          <div className="flex justify-center py-24">
            <Loader2 size={28} className="animate-spin text-blue-500" aria-label="Loading" />
          </div>
        )}

        {authed === false && (
          <div className="mx-auto max-w-sm border border-white/10 bg-panel p-8" data-testid="admin-login-card">
            <Lock size={24} strokeWidth={1.8} className="text-blue-500" aria-hidden="true" />
            <h1 className="mt-4 font-display text-3xl font-bold uppercase tracking-wide text-white">Owner Access</h1>
            <p className="mt-2 text-sm text-slate-400">Enter your PIN to view quote requests and manage photos.</p>
            <form onSubmit={login} className="mt-6">
              <label htmlFor="pin" className="field-label">PIN</label>
              <input
                id="pin"
                type="password"
                inputMode="numeric"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                className="field-input"
                autoComplete="off"
                data-testid="admin-pin-input"
              />
              {error && (
                <p className="mt-3 border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300" role="alert" data-testid="admin-login-error">
                  {error}
                </p>
              )}
              <button type="submit" className="btn-primary mt-5 w-full" data-testid="admin-login-button">
                Unlock Dashboard
              </button>
            </form>
          </div>
        )}

        {authed === true && (
          <>
            <div className="flex items-center gap-2 border-b border-white/10" role="tablist">
              {[["leads", "Quote Requests"], ["photos", "Real Results Photos"]].map(([key, name]) => (
                <button
                  key={key}
                  role="tab"
                  aria-selected={tab === key}
                  onClick={() => setTab(key)}
                  className={`px-4 py-3 font-display text-lg font-semibold uppercase tracking-wide transition-colors ${
                    tab === key ? "border-b-2 border-blue-500 text-white" : "text-slate-500 hover:text-slate-300"
                  }`}
                  data-testid={`admin-tab-${key}`}
                >
                  {name}
                </button>
              ))}
            </div>

            {tab === "leads" && (
              <div className="mt-8" data-testid="admin-leads-panel">
                <div className="mb-5 flex items-center justify-between">
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{leads.length} request{leads.length === 1 ? "" : "s"}</p>
                  <button onClick={loadLeads} className="flex items-center gap-2 border border-white/15 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-300 hover:border-blue-500/50" data-testid="admin-refresh-leads">
                    <RefreshCw size={13} /> Refresh
                  </button>
                </div>
                {leads.length === 0 ? (
                  <p className="border border-dashed border-white/15 p-10 text-center text-sm text-slate-500">No quote requests yet. New form submissions appear here and in your email.</p>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {leads.map((lead) => (
                      <LeadCard key={lead.id} lead={lead} onBook={bookLead} onUnbook={unbookLead} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "photos" && (
              <div className="mt-8" data-testid="admin-photos-panel">
                <form onSubmit={upload} className="border border-white/10 bg-panel p-6 sm:p-8" data-testid="admin-upload-form">
                  <h2 className="flex items-center gap-2 font-display text-2xl font-bold uppercase tracking-wide text-white">
                    <ImagePlus size={20} className="text-blue-500" aria-hidden="true" /> Add Before / After Photos
                  </h2>
                  <p className="mt-2 text-sm text-slate-400">Upload straight from your phone after a job. They appear in the Real Results section immediately.</p>
                  <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
                    <div>
                      <label htmlFor="photo-label" className="field-label">Label</label>
                      <input id="photo-label" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} placeholder="e.g. F-150 Interior" className="field-input" data-testid="admin-photo-label" />
                    </div>
                    <div>
                      <label htmlFor="photo-before" className="field-label">Before Photo</label>
                      <input id="photo-before" type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setBeforeFile(e.target.files?.[0] || null)} className="field-input file:mr-3 file:border-0 file:bg-blue-600 file:px-3 file:py-1.5 file:font-mono file:text-[10px] file:uppercase file:tracking-widest file:text-white" data-testid="admin-photo-before" />
                    </div>
                    <div>
                      <label htmlFor="photo-after" className="field-label">After Photo</label>
                      <input id="photo-after" type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setAfterFile(e.target.files?.[0] || null)} className="field-input file:mr-3 file:border-0 file:bg-blue-600 file:px-3 file:py-1.5 file:font-mono file:text-[10px] file:uppercase file:tracking-widest file:text-white" data-testid="admin-photo-after" />
                    </div>
                  </div>
                  <button type="submit" disabled={uploading} className="btn-primary mt-6 disabled:opacity-60" data-testid="admin-upload-button">
                    {uploading ? (<><Loader2 size={16} className="animate-spin" /> Uploading…</>) : "Publish Photos"}
                  </button>
                </form>

                <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {photos.map((p) => (
                    <article key={p.id} className="border border-white/10 bg-panel p-4" data-testid={`admin-photo-${p.id}`}>
                      <div className="grid grid-cols-2 gap-2">
                        <img src={`${API}/gallery/image/${p.id}/before`} alt={`${p.label} before`} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                        <img src={`${API}/gallery/image/${p.id}/after`} alt={`${p.label} after`} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div>
                          <p className="font-display text-lg font-semibold uppercase tracking-wide text-white">{p.label}</p>
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{fmtDate(p.created_at)}</p>
                        </div>
                        <button onClick={() => removePhoto(p.id)} className="flex items-center gap-1.5 border border-red-500/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-red-300 hover:bg-red-500/10" data-testid={`admin-photo-delete-${p.id}`}>
                          <Trash2 size={13} /> Remove
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
                {photos.length === 0 && (
                  <p className="mt-6 border border-dashed border-white/15 p-10 text-center text-sm text-slate-500">No photos yet. Upload your first before/after set above.</p>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Admin;
